import { Button, IconButton, Typography } from "@povio/ui";
import clsx from "clsx";
import Image from "next/image";
import { useState } from "react";

import { EmailIcon } from "@/assets/icons/general/Email";
import { FacebookIcon } from "@/assets/icons/general/Facebook";
import { InstagramIcon } from "@/assets/icons/general/Instagram";
import { LinkedinIcon } from "@/assets/icons/general/Linkedin";
import { PhoneIcon } from "@/assets/icons/general/Phone";
import { YoutubeIcon } from "@/assets/icons/general/Youtube";
import { PlayVideoIcon } from "@/assets/icons/ui/PlayVideo";
import DefaultAvatar from "@/assets/images/general/default-avatar.png";
import BackgroundImage from "@/assets/images/project/bg.png";
import PhoneImage from "@/assets/images/project/phone.png";
// eslint-disable-next-line no-restricted-imports
import IntroductionFormModal from "@/components/home/modals/IntroductionFormModal";
import type { ISectionProps } from "@/components/home/sections/ProfileBioSection";
import ProfileSectionCard from "@/components/home/sections/ProfileSectionCard";
import useMediaRender from "@/components/home/sections/useMediaRender";
import { DemoClickWrapper } from "@/components/shared/demo/DemoClickWrapper";
import { ModalSheet } from "@/components/shared/modal/ModalSheet";
import { TourSelectors } from "@/config/tour.config";
import type { ProfileModels } from "@/data/profile/profile.models";
import { useShowMore } from "@/hooks/useShowMore";
import { DemoContext } from "@/providers/demo/DemoContext";
import { NumberUtils } from "@/util/number.utils";
import { ProfileUtils } from "@/util/profile.utils";

interface IProps extends ISectionProps {
  userProfile?: ProfileModels.FullProfileResponseDto | null;
  userName?: string;
}

const ProfileIntroductionSection = ({ userProfile, isPublic = false, userName }: IProps) => {
  const { isDemoMode } = DemoContext.useDemo();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isShowMoreOpen, setIsShowMoreOpen] = useState(false);
  const [isEmbeddedVideoOpen, setIsEmbeddedVideoOpen] = useState(false);

  const { ref: textRef, showMore } = useShowMore("line-clamp");

  const summary = userProfile?.introductionBio;
  const name = userProfile?.introductionTitle || userName;

  const email = userProfile?.dataV02?.person?.profile?.contact?.email;
  const phone = userProfile?.dataV02?.person?.profile?.contact?.phone;

  const profileSocials = ProfileUtils.getProfileSocials(userProfile);

  const onEditIntroduction = () => {
    setIsModalOpen(true);
  };

  const closeProjectModal = () => {
    setIsModalOpen(false);
  };

  const videoUrl = userProfile?.introductionVideoUrl;

  const renderedMedia = useMediaRender(videoUrl);

  const onEmbeddedVideoStart = () => {
    setIsEmbeddedVideoOpen(true);
  };

  if (!userProfile?.introductionVideoUrl && !summary && !name) {
    return (
      <div
        id={TourSelectors.IntroductionSection}
        className="flex dd:h-[360px] dd:w-1/2 w-full flex-col items-center justify-center gap-4 rounded-sm bg-cover bg-no-repeat dd:p-0 p-4"
        style={{ backgroundImage: `url(${BackgroundImage.src})` }}
      >
        <div className="flex flex-row flex-wrap items-end justify-center gap-4">
          <Image
            src={PhoneImage}
            alt="Laptop"
            width={150}
            height={300}
            className="h-auto max-w-[150px] object-contain [@media(min-width:80rem)_and_(max-width:82.5rem)]:max-h-[200px]"
          />
          <div className="flex max-w-[223px] m:-translate-y-4 flex-col items-center justify-center gap-4">
            <Typography
              size="body-1"
              variant="prominent-2"
              className="m:text-left text-center text-secondary-75 leading-6"
            >
              Upload cover image or <span className="text-primary">Introduction</span> video.
            </Typography>
            <DemoClickWrapper>
              <Button
                color="secondary"
                size="s"
                isDisabled={isDemoMode}
                onPress={onEditIntroduction}
                className="w-full px-[24px]! py-[9px]! text-label-1!"
              >
                Upload Now
              </Button>
            </DemoClickWrapper>
          </div>
        </div>
      </div>
    );
  }

  return (
    <ProfileSectionCard
      title="Introduction Bio"
      titleSize="title-3"
      isPublic={isPublic}
      containerClassName="flex flex-col gap-4"
      onEditSectionClicked={onEditIntroduction}
      titleId={TourSelectors.IntroductionSection}
    >
      <div className="flex dd:flex-row flex-col items-start justify-start dd:gap-6 gap-3">
        {!userProfile?.introductionVideoUrl && !userProfile?.introductionCoverImageUrl && (
          <div
            id={TourSelectors.IntroductionSection}
            className="flex dd:h-[360px] dd:w-1/2 w-full flex-col items-center justify-end gap-4 rounded-sm bg-cover bg-no-repeat dd:p-0 p-4 pb-4! t:pb-0!"
            style={{ backgroundImage: `url(${BackgroundImage.src})` }}
          >
            <div className="flex flex-row flex-wrap items-end justify-center gap-4 t:gap-12">
              <Image
                src={PhoneImage}
                alt="Laptop"
                width={150}
                height={300}
                className="h-auto max-w-[150px] object-contain [@media(min-width:80rem)_and_(max-width:82.5rem)]:max-h-[200px]"
              />
              <div className="flex max-w-[223px] flex-col items-center justify-center gap-4 self-center">
                <Typography
                  size="body-1"
                  variant="prominent-2"
                  className="m:text-left text-center text-secondary-75 leading-6"
                >
                  Upload cover image or <span className="text-primary">Introduction</span> video.
                </Typography>
                <DemoClickWrapper>
                  <Button
                    color="secondary"
                    size="s"
                    isDisabled={isDemoMode}
                    onPress={onEditIntroduction}
                    className="w-full px-[24px]! py-[9px]! text-label-1!"
                  >
                    Upload Now
                  </Button>
                </DemoClickWrapper>
              </div>
            </div>
          </div>
        )}

        {(userProfile?.introductionVideoUrl || userProfile?.introductionCoverImageUrl) && (
          <div className="group relative h-[342px] dd:max-h-[400px] dd:w-[50%] dm:w-full dd:min-w-[50%] dd:overflow-hidden dd:rounded-button-rounding">
            {!isEmbeddedVideoOpen && (
              <>
                <Image
                  src={
                    userProfile?.introductionCoverImageUrl ||
                    userProfile?.introductionVideoThumbnailUrl ||
                    DefaultAvatar.src
                  }
                  alt="Introduction video thumbnail"
                  className="h-[100px] w-full rounded-ml object-cover"
                  fill
                  data-testid="introduction-video-thumbnail"
                />
                {videoUrl && (
                  <IconButton
                    icon={PlayVideoIcon}
                    variant="contained"
                    color="secondary"
                    label="play"
                    size="s"
                    className="absolute top-1/2 left-1/2 z-10 -translate-x-1/2 -translate-y-1/2"
                    onPress={onEmbeddedVideoStart}
                    data-testid="play-introduction-video-button"
                  />
                )}
              </>
            )}
            {isEmbeddedVideoOpen && renderedMedia}
          </div>
        )}
        <div
          className={clsx(
            "flex dd:h-[342px] dd:max-h-[400px] min-h-fit dd:w-1/2 dd:min-w-[30%] flex-col",
            !userProfile?.introductionCoverImageUrl && !userProfile?.introductionVideoUrl && "dd:h-[327px]!",
          )}
        >
          {name && (
            <Typography
              size="title-3"
              className="dd:mb-4 mb-3"
              data-testid="introduction-user-name"
            >
              {name}
            </Typography>
          )}
          {summary && (
            <Typography
              ref={textRef}
              variant="default"
              size="body-4"
              className="line-clamp-4 text-text-default-2"
              data-testid="introduction-user-bio"
            >
              {summary}
            </Typography>
          )}
          {showMore && (
            <div>
              <Button
                variant="text"
                size="none"
                onPress={() => setIsShowMoreOpen(true)}
              >
                Show More
              </Button>
            </div>
          )}
          <div className="my-6 flex t:flex-row flex-col t:items-center gap-6 t:gap-0 xl:my-auto">
            {email && (
              <Typography
                size="label-2"
                variant="prominent-1"
                className="flex w-full max-w-60 items-center gap-2 text-interactive-text-secondary-idle"
              >
                <EmailIcon className="h-6 w-6" />
                {email}
              </Typography>
            )}
            {phone && (
              <Typography
                size="label-2"
                variant="prominent-1"
                className="flex w-full max-w-60 items-center gap-2 text-interactive-text-secondary-idle"
              >
                <PhoneIcon className="h-6 w-6" />
                {NumberUtils.formatPhoneNumber(phone?.replace(/\D/g, "") || "", true)}
              </Typography>
            )}
          </div>
          <div className="flex flex-row gap-4">
            {profileSocials.linkedin && (
              <IconButton
                icon={LinkedinIcon}
                onPress={() => window.open(profileSocials.linkedin, "_blank")}
                label="Linkedin Social Link"
                size="none"
                color="secondary"
                variant="ghost"
                data-testid="linkedin-social-link-button"
              />
            )}
            {profileSocials.instagram && (
              <IconButton
                icon={InstagramIcon}
                onPress={() => window.open(profileSocials.instagram, "_blank")}
                label="Instagram Social Link"
                size="none"
                color="secondary"
                variant="ghost"
                data-testid="instagram-social-link-button"
              />
            )}
            {profileSocials.youtube && (
              <IconButton
                icon={<YoutubeIcon className="w-[31px]" />}
                onPress={() => window.open(profileSocials.youtube, "_blank")}
                label="Youtube Social Link"
                size="none"
                color="secondary"
                variant="ghost"
                data-testid="youtube-social-link-button"
              />
            )}
            {profileSocials.facebook && (
              <IconButton
                icon={FacebookIcon}
                onPress={() => window.open(profileSocials.facebook, "_blank")}
                label="Facebook Social Link"
                size="none"
                color="secondary"
                variant="ghost"
                data-testid="facebook-social-link-button"
              />
            )}
          </div>
        </div>
      </div>
      {isModalOpen && (
        <IntroductionFormModal
          isOpen={isModalOpen}
          onClose={closeProjectModal}
          userProfile={userProfile}
        />
      )}
      <ModalSheet
        title={name}
        isOpen={isShowMoreOpen}
        onClose={() => setIsShowMoreOpen(false)}
      >
        <Typography
          variant="default"
          size="body-4"
          className="text-text-default-2"
          data-testid="introduction-user-bio"
        >
          {summary}
        </Typography>
      </ModalSheet>
    </ProfileSectionCard>
  );
};

export default ProfileIntroductionSection;
